import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
const Modal = ({ isOpen, onClose, title, children, size = 'md', showCloseButton = true, disableClickOutside = false }) => {
    const modalRef = useRef(null);
    // Focar no modal quando abrir para acessibilidade
    useEffect(() => {
        if (isOpen && modalRef.current) {
            modalRef.current.focus();
        }
    }, [isOpen]);
    // Fechar modal ao clicar fora (conforme solicitado)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!disableClickOutside && modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose, disableClickOutside]);
    // Fechar modal com ESC
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscapeKey);
        }
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, onClose]);
    if (!isOpen)
        return null;
    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        '2xl': 'max-w-6xl'
    };
    return (_jsxs("div", { className: "fixed inset-0 z-50 overflow-y-auto", role: "dialog", "aria-modal": "true", "aria-labelledby": title ? "modal-title" : undefined, "aria-describedby": "modal-content", children: [_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 transition-opacity", "aria-hidden": "true" }), _jsx("div", { className: "flex min-h-full items-center justify-center p-4", children: _jsxs("div", { ref: modalRef, className: `
            relative w-full ${sizeClasses[size]} 
            transform overflow-hidden rounded-lg bg-white 
            shadow-xl transition-all
            animate-fade-in
          `, role: "document", tabIndex: -1, children: [(title || showCloseButton) && (_jsxs("div", { className: "flex items-center justify-between border-b border-gray-200 px-6 py-4", children: [_jsx("h3", { id: "modal-title", className: "text-lg font-semibold text-gray-900", children: title }), showCloseButton && (_jsx("button", { onClick: onClose, className: "\n                    rounded-md p-1 text-gray-400 hover:text-gray-600 \n                    focus:outline-none focus:ring-2 focus:ring-primary-500\n                    transition-colors\n                  ", "aria-label": "Fechar modal", children: _jsx(X, { className: "h-5 w-5" }) }))] })), _jsx("div", { id: "modal-content", className: "px-6 py-4", children: children })] }) })] }));
};
export const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = 'Confirmar', cancelText = 'Cancelar', type = 'danger' }) => {
    const typeStyles = {
        danger: {
            button: 'bg-danger-600 hover:bg-danger-700 focus:ring-danger-500',
            icon: '⚠️'
        },
        warning: {
            button: 'bg-warning-600 hover:bg-warning-700 focus:ring-warning-500',
            icon: '⚠️'
        },
        info: {
            button: 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500',
            icon: 'ℹ️'
        }
    };
    const handleConfirm = () => {
        onConfirm();
        onClose();
    };
    return (_jsx(Modal, { isOpen: isOpen, onClose: onClose, title: title, size: "sm", children: _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex items-start space-x-3", children: [_jsx("div", { className: "text-2xl", children: typeStyles[type].icon }), _jsx("p", { className: "text-gray-600", children: message })] }), _jsxs("div", { className: "flex justify-end space-x-3 pt-4", children: [_jsx("button", { onClick: onClose, className: "\n              px-4 py-2 text-sm font-medium text-gray-700 \n              bg-white border border-gray-300 rounded-md \n              hover:bg-gray-50 focus:outline-none focus:ring-2 \n              focus:ring-offset-2 focus:ring-primary-500\n              transition-colors\n            ", "aria-label": `${cancelText} e fechar modal`, children: cancelText }), _jsx("button", { onClick: handleConfirm, className: `
              px-4 py-2 text-sm font-medium text-white 
              ${typeStyles[type].button}
              border border-transparent rounded-md 
              focus:outline-none focus:ring-2 focus:ring-offset-2
              transition-colors
            `, "aria-label": `${confirmText} ação`, children: confirmText })] })] }) }));
};
export const LoadingModal = ({ isOpen, message = 'A carregar...' }) => {
    return (_jsx(Modal, { isOpen: isOpen, onClose: () => { }, showCloseButton: false, size: "sm", children: _jsxs("div", { className: "flex items-center justify-center space-x-3 py-8", role: "status", "aria-live": "polite", children: [_jsx("div", { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600", "aria-hidden": "true" }), _jsx("span", { className: "text-gray-600", "aria-label": `A carregar: ${message}`, children: message })] }) }));
};
export default Modal;
